import { Alert } from '@ps/isp-web-component-library';
import { Translation } from '@ps/web-shared-components';
import React from 'react';
import { useField } from 'react-final-form';
import { useIntl } from 'react-intl';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import TranslationWithLink from '@swf/sports/components/TranslationWithLink/TranslationWithLink';

import useDismissibleBetSlipError from '../../../../../../hooks/useDismissibleBetSlipError';

import styles from './ErrorBanner.scss';

const ErrorBanner: React.FC<{ fieldName: string }> = ({ fieldName }) => {
  const {
    meta: { submitError }
  } = useField<OpportunityInput | undefined>(fieldName, {
    subscription: { submitError: true }
  });
  const { contentTranslationTag: betSlipLevelTranslationTag, errorResponse } =
    useDismissibleBetSlipError();
  const { formatMessage } = useIntl();

  if (!submitError || !betSlipLevelTranslationTag) {
    return null;
  }

  let currencyAmountValue;
  const placeBetResult = errorResponse?.result?.find(
    (result) => result.betNo === submitError.betNo
  );
  if (placeBetResult) {
    const { resultCode, betMinStake, betMaxPayout, betMinStakeIncrement } = placeBetResult;

    switch (resultCode) {
      case 'STAKE_BELOW_MINIMUM_ALLOWED':
        currencyAmountValue = betMinStake;
        break;
      case 'PAYOUT_ABOVE_MAXIMUM_ALLOWED':
        currencyAmountValue = betMaxPayout;
        break;
      case 'STAKE_INCREMENT_INVALID':
        currencyAmountValue = betMinStakeIncrement;
        break;
    }
  }

  const { titleTranslationTag, contentTranslationTag, link } = submitError.errorMessage;

  if (currencyAmountValue) {
    return (
      <span className={styles.container}>
        <Translation
          {...contentTranslationTag}
          values={{
            0: <CurrencyAmount value={currencyAmountValue} dollarValue />
          }}
        />
      </span>
    );
  }

  const isCollapsible = !!titleTranslationTag;
  if (isCollapsible) {
    return (
      <Alert
        isCollapsible
        title={formatMessage(titleTranslationTag)}
        level="destructive"
        variant="aggressive"
        isSquare
      >
        <TranslationWithLink contentTranslationTag={contentTranslationTag} link={link} />
      </Alert>
    );
  }

  return (
    <span className={styles.container}>
      <TranslationWithLink contentTranslationTag={contentTranslationTag} link={link} />
    </span>
  );
};

export default ErrorBanner;
